import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { media } from 'src/config/breakpoints';
import { SectionTitle } from 'src/common/components/SectionTitle';
import { typography } from 'src/config/typography';
import { colors } from 'src/config/colors';
import { Speaker, SpeakerUrlType } from 'src/common/models/speaker';
import { speakerFullName } from 'src/common/services/speaker';
import { CleanButton } from 'src/common/components/Button';
import SpeakerUrlFacebookSvg from 'src/assets/images/speaker-url-facebook.svg';
import speakerUrlInstagramPng from 'src/assets/images/speaker-url-instagram.png';
import SpeakerUrlLinkedinSvg from 'src/assets/images/speaker-url-linkedin.svg';
import SpeakerUrlWwwSvg from 'src/assets/images/speaker-url-www.svg';
import SpeakerUrlYoutubeSvg from 'src/assets/images/speaker-url-youtube.svg';
import Image from 'next/image';

const Container = styled.div`
  ${media.w.min.px768(css`
    align-items: start;
    display: grid;
    grid-template-areas:
      'image content'
      'urls content';
    grid-template-columns: 10.4375rem minmax(0, 1fr);
    grid-template-rows: min-content minmax(0, 1fr);
  `)};

  ${media.w.min.px1200(css`
    align-self: flex-start;
    align-items: flex-start;
    margin-top: 4.375rem;
  `)};
`;

const Description = styled.div`
  grid-area: content;
  margin-top: 1.875rem;
  padding-bottom: 3.75rem;

  ${media.w.min.px768(css`
    margin-top: 0;
    margin-left: 1.875rem;
    max-width: 25.75rem;
    padding-bottom: 3.25rem;
  `)}

  ${media.w.min.px1200(css`
    padding-bottom: 4.5rem;
  `)}

  ${SectionTitle} {
    margin-bottom: 0;
    margin-top: 0.3125rem;
  }
`;

const ImageContainer = styled.div`
  height: 7.5rem;
  grid-area: image;
  margin: 0 auto;
  position: relative;
  width: 7.5rem;

  ${media.w.min.px768(css`
    height: 10.4375rem;
    width: 10.4375rem;
  `)};

  ${media.w.min.px1200(css`
    margin-top: -2.5rem;
  `)};
`;

const ImageDesktop = styled(Image)`
  border-radius: 50%;
`;

const StyledSectionTitle = styled(SectionTitle)`
  ${media.w.max.px768(css`
    text-align: center;
  `)}
`;

const Text = styled.div`
  font-family: ${typography.fontFamilyDefault};
  color: ${colors.blue};
  font-size: 0.75rem;
  line-height: 1.25rem;
  margin-top: 1.5rem;
  white-space: pre-line;
`;

const Trainer = styled.div`
  color: ${colors.blue};
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1.5rem;
  line-height: 1.875rem;
  text-align: center;

  ${media.w.min.px768(css`
    font-size: 2.3125rem;
    line-height: 2.875rem;
    text-align: left;
  `)}
`;

const Urls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.625rem;
  grid-area: urls;
  justify-content: center;
  margin: 1.25rem 0 -0.625rem;

  ${media.w.min.px768(css`
    padding: 0 0.625rem;
  `)}
`;

const UrlsItem = styled(CleanButton)`
  align-items: center;
  background: ${colors.porcelain};
  border-radius: 50%;
  display: flex;
  flex: 0 0 auto;
  height: 2rem;
  justify-content: center;
  width: 2rem;

  img,
  svg {
    display: block;
    width: 1.125rem;
  }
`;

const urlIconMap = {
  [SpeakerUrlType.Facebook]: <SpeakerUrlFacebookSvg />,
  [SpeakerUrlType.Instagram]: <Image src={speakerUrlInstagramPng} />,
  [SpeakerUrlType.Linkedin]: <SpeakerUrlLinkedinSvg />,
  [SpeakerUrlType.Web]: <SpeakerUrlWwwSvg />,
  [SpeakerUrlType.Youtube]: <SpeakerUrlYoutubeSvg />,
};

export interface SpeakerProfileCardProps {
  speaker: Speaker;
}

export const SpeakerProfileCard: FC<SpeakerProfileCardProps> = ({ speaker }) => (
  <Container>
    <ImageContainer>
      {speaker.photo != null && (
        <ImageDesktop
          alt={speakerFullName(speaker)}
          layout="fill"
          objectFit="contain"
          src={speaker.photo.url}
        />
      )}
    </ImageContainer>

    {speaker.urls.length > 0 && (
      <Urls>
        {speaker.urls.map((url) => (
          <React.Fragment key={url.urlType}>
            {url.address.length > 0 && (
              <UrlsItem as="a" href={url.address} rel="noreferrer" target="_blank">
                {urlIconMap[url.urlType]}
              </UrlsItem>
            )}
          </React.Fragment>
        ))}
      </Urls>
    )}

    <Description>
      <Trainer>{speakerFullName(speaker)}</Trainer>
      <StyledSectionTitle>{speaker.company}</StyledSectionTitle>
      <Text>{speaker.bio}</Text>
    </Description>
  </Container>
);
